import { useState, useEffect } from "react";
import { Button, Container, Grid, Stack, Text } from "basis";
import { usePageTracking } from "react-event-tracker";
import { postAccountLink } from "library/api";
import { isAlpha } from "library/validator";
import { Layout } from "../../Layout/Layout";
import { MaskedInput } from "../../MaskedInput/MaskedInput";
import { Input } from "../../Input/Input";
import { SvgInline } from "../../SvgInline/SvgInline";
import { LinkErrorMessage } from "./LinkErrorMessage";
import {
  getFatalErrorContent,
  PERSONAL_LOAN_ERRORS,
} from "../LinkingErrorContent";
import * as contacts from "../../../constants/contacts";

export const PersonalLoan = ({ setScreen, params }) => {
  const [state, setState] = useState({
    firstName: "",
    accountNumber: "",
    dob: "",
    fieldError: false,
    loading: false,
    pageError: false,
    errorMsg: "",
    errorEnum: "",
    fatalError: false,
    showName: false,
  });
  const updateState = (newState) => setState({ ...state, ...newState });
  const {
    firstName,
    accountNumber,
    loading,
    fieldError,
    dob,
    pageError,
    errorEnum,
    fatalError,
    showName,
  } = state;
  const { currentStep, backClick } = params;

  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "accounts",
      siteSubSection: "link-product-personal-loan",
      pageTitle: "details",
    },
    user: {
      authenticationStatus: "logged in",
    },
  });

  const onNextClick = async (event) => {
    event.preventDefault();

    updateState({ loading: true });

    // convert date of birth
    const dateOfBirth = dob.split(" / ").reverse().join("-");
    const newState = {
      loading: false,
    };

    try {
      const { data } = await postAccountLink({
        data: {
          type: "PERSONAL_LOAN",
          personal_loan: {
            account_number: accountNumber,
            date_of_birth: dateOfBirth,
            first_name: firstName,
          },
        },
      });

      return setScreen("OTP", {
        ...params,
        productId: data.link.id,
        siteSubSection: "link-product-personal-loan",
        currentStep: currentStep + 1,
        linkId: data.link.id,
        accountNumber: data.link.personal_loan.account_number,
        maskedMobile: data.link.masked_mobile,
      });
    } catch (e) {
      if (e.response?.data?.type === "ADDITIONAL_INFORMATION_REQUIRED") {
        newState.showName = true;
      }

      if (e.response?.data) {
        newState.errorMsg =
          PERSONAL_LOAN_ERRORS[e.response.data.type] || e.response.data.message;
        newState.errorEnum = e.response?.data.type;

        // check for fatal error
        newState.fatalError = getFatalErrorContent(e.response.data);
      }

      // Cannot conditionally call hook functions so need to use intermediate variable
      newState.pageError = true;
      newState.fieldError = true;
    }

    updateState(newState);

    return null;
  };

  useEffect(() => {
    if (fatalError) {
      setScreen("FatalError", {
        ...params,
        error: fatalError,
      });
    }
  }, [fatalError, params, setScreen]);

  return (
    <Layout>
      <Layout.Header>Link personal loan account</Layout.Header>
      <Layout.Main>
        <form>
          <Stack gap="6" width="360">
            <Text as="h3" textStyle="heading5">
              Account details
            </Text>
            <Input
              label="Account number"
              value={accountNumber}
              onChange={(e) => {
                // return non-digit values (will let backspace etc. through)
                if (/\D/.test(e.target.value)) {
                  return;
                }

                updateState({
                  fieldError: false,
                  accountNumber: e.target.value,
                });
              }}
              error={fieldError}
            />
            <MaskedInput
              maskChar=" "
              label="Date of birth"
              placeholder="DD / MM / YYYY"
              mask="99 / 99 / 9999"
              value={dob}
              onChange={(e) =>
                updateState({ fieldError: false, dob: e.target.value })
              }
              error={fieldError}
            />
            {showName && (
              <Input
                label="First name"
                validationErrorMessages={{
                  empty: "Enter first name",
                  invalid: "Enter a valid name",
                }}
                validator={isAlpha}
                value={firstName}
                onChange={(e) =>
                  updateState({
                    fieldError: !e.success,
                    firstName: e.value.replace(/\s/g, ""),
                  })
                }
                error={fieldError}
              />
            )}
            {pageError && <LinkErrorMessage errorEnum={errorEnum} />}
            <Stack gap="6" direction-xs="horizontal">
              <Button
                type="submit"
                width="168"
                loading={loading}
                disabled={fieldError || !dob || !accountNumber}
                onClick={onNextClick}
              >
                Next
              </Button>
              <Button
                variant="secondary"
                width="168"
                onClick={() =>
                  backClick ? setScreen(backClick) : setScreen("AccountType")
                }
              >
                Back
              </Button>
            </Stack>
          </Stack>
        </form>
      </Layout.Main>
      <Layout.Sidebar>
        <Stack gap="6">
          <Text as="h4" textStyle="heading5">
            Where is my account number?
          </Text>
          <Grid cols="2">
            <Grid.Item colSpan="0">
              <Stack gap="6">
                <Text>
                  You can find it on your Statements, in the top right corner as
                  can be seen to the right.
                </Text>
                <Text>Alternatively, call for support:</Text>
                <Container>
                  <Text>
                    <strong>Calling from Australia</strong>
                  </Text>
                  <Text textStyle="subtitle2" color="primary.blue.t100">
                    <strong>{contacts.LINKING_LOAN_SUPPORT.AU.NUM}</strong>
                  </Text>
                  <Text>
                    Mon - Fri: {contacts.LINKING_LOAN_SUPPORT.AU.HOURS_WEEKDAY}
                  </Text>
                  <Text>
                    Weekends: {contacts.LINKING_LOAN_SUPPORT.AU.HOURS_WEEKEND}
                  </Text>
                </Container>
                <Container>
                  <Text>
                    <strong>Calling from New Zealand</strong>
                  </Text>
                  <Text textStyle="subtitle2" color="primary.blue.t100">
                    <strong>{contacts.LINKING_LOAN_SUPPORT.NZ.NUM}</strong>
                  </Text>
                  <Text>
                    Mon - Fri: {contacts.LINKING_LOAN_SUPPORT.NZ.HOURS_WEEKDAY}
                  </Text>
                  <Text>
                    Weekends: {contacts.LINKING_LOAN_SUPPORT.NZ.HOURS_WEEKEND}
                  </Text>
                </Container>
              </Stack>
            </Grid.Item>
            <Grid.Item colSpan="1">
              <SvgInline name="account-number" size="256" />
            </Grid.Item>
          </Grid>
        </Stack>
      </Layout.Sidebar>
    </Layout>
  );
};
